import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Typography, Box, Paper, Button, CircularProgress, IconButton, Snackbar, Alert } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AdminUserForm from '../components/Admin/AdminUserForm';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

function EditAdminUserPage() {
  const { adminId } = useParams();
  const navigate = useNavigate();
  const { currentUser, isFullAdmin } = useAuth(); // Need current user to prevent self-delete
  const [adminData, setAdminData] = useState(null);
  const [availableApps, setAvailableApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  useEffect(() => {
    // Redirect if not a full admin
    if (!isFullAdmin()) {
      navigate('/select-app');
      return;
    }

    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        // Fetch admin details AND all apps concurrently
        const [adminRes, appsRes] = await Promise.all([
          axios.get(`/api/admins/${adminId}`),
          axios.get('/api/apps') // Full admins get all apps
        ]);
        setAdminData(adminRes.data);
        setAvailableApps(appsRes.data);
      } catch (err) {
        console.error('Failed to fetch data for editing admin user:', err);
        setError(err.response?.data?.message || 'Could not load user data or applications.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [adminId, isFullAdmin, navigate]);

  const handleUpdateUser = async (payload) => {
    try {
      await axios.put(`/api/admins/${adminId}`, payload);
      setSnackbarMessage('User updated successfully!');
      setSnackbarOpen(true);
      // Optionally navigate back after a delay or keep user here
      // navigate('/select-app'); 
    } catch (err) {
      console.error('Update admin user error:', err);
      throw new Error(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    setDeleteError('');
    if (currentUser?.id === parseInt(adminId, 10)) {
      setDeleteError('You cannot delete your own account.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await axios.delete(`/api/admins/${adminId}`);
        navigate('/select-app'); // Redirect after successful deletion
      } catch (err) {
        console.error('Delete admin user error:', err);
        setDeleteError(err.response?.data?.message || 'Failed to delete user.');
      }
    }
  };
  
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  if (!isFullAdmin()) {
    return null; // Should have been redirected
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ my: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Button 
            startIcon={<ArrowBackIcon />} 
            onClick={() => navigate('/select-app')} // Go back to app selection
          >
            Back to App Selection
          </Button>
          <IconButton 
            color="error" 
            onClick={handleDeleteUser} 
            title="Delete User"
            disabled={currentUser?.id === parseInt(adminId, 10)} // Disable self-delete
          >
            <DeleteIcon />
          </IconButton>
        </Box>
        
        {deleteError && <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>}
        
        <Paper sx={{ p: 3 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Edit Admin User
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : error ? (
            <Typography color="error">{error}</Typography>
          ) : adminData ? (
            <AdminUserForm 
              initialData={adminData} 
              onSubmit={handleUpdateUser} 
              isEditing={true} 
              availableApps={availableApps}
            />
          ) : (
             <Typography color="error">User data could not be loaded.</Typography> // Should not happen if loading is false and no error
          )}
        </Paper>
      </Box>
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default EditAdminUserPage; 